import { useState } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import { Alert, Button } from '@mui/material';

import { PendingBookingActions } from './PendingBookingActions';
import { ReviewIdentityDialog } from './ReviewIdentityDialog';

export interface SelfBookedReviewBannerProps {
  appointmentId: string;
  bookingProspectId: string;
  /** Appointment status — 'suggested' means confirm/decline still apply. */
  status?: string | null;
  /** Called after the booking is declined (the appointment is cancelled). */
  onDeclined?: () => void;
}

/** Detail-drawer banner for a self-booked appointment without a chart.
 *
 * While the booking is pending it carries confirm/decline plus the identity
 * review entry point; once confirmed (but still unpromoted) it keeps the
 * review entry point so the "Unassigned" state is always actionable.
 */
export function SelfBookedReviewBanner({
  appointmentId,
  bookingProspectId,
  status,
  onDeclined,
}: SelfBookedReviewBannerProps) {
  const [reviewOpen, setReviewOpen] = useState(false);
  const { singular } = useSubjectLanguageVariants();

  // Only two states carry a banner: pending ('suggested') and confirmed-but-
  // unpromoted ('scheduled' — the confirmed status value). A declined
  // booking's prospect is discarded, so review must not be offered.
  if (status !== 'suggested' && status !== 'scheduled') {
    return null;
  }

  return (
    <>
      {status === 'suggested' ? (
        <PendingBookingActions
          appointmentId={appointmentId}
          bookingProspectId={bookingProspectId}
          source="drawer"
          onResolved={action => {
            if (action === 'declined') {
              onDeclined?.();
            }
          }}
          onReviewIdentity={() => setReviewOpen(true)}
        />
      ) : (
        <Alert
          severity="info"
          icon={false}
          sx={{ mb: 2 }}
          action={
            <Button
              size="small"
              startIcon={<PersonSearchOutlinedIcon />}
              onClick={() => setReviewOpen(true)}
            >
              Review identity
            </Button>
          }
        >
          Self-booked appointment — no {singular} chart linked yet.
        </Alert>
      )}
      <ReviewIdentityDialog
        bookingProspectId={bookingProspectId}
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        source="drawer"
      />
    </>
  );
}
